import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { ArrowRight } from "@mui/icons-material";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Loading,
  PaymentHeader,
  PaymentMethods,
  PaymentSummary,
  PhoneNumberInputs,
} from "..";
import StepContainer from "../StepContainer";
import MerchantInfo from "../merchantInfo";
import SearchCustomerForm from "../SearchCustomerForm";
import ServiceChargeSummary from "../ServiceChargeSummary";
import ConfiguredFields from "../fields/ConfiguredFields";
import CustomInput from "../inputs/cutsomInput";
import Card from "../card";
import Chip from "../chip";
import StackList from "../stackList";
import { useDimensions, usePayment } from "../../hooks";
import {
  priceSearch,
  useQueryPaymentMethods,
  usePriceMutation,
} from "../../api/queries";
import { usePayMutation } from "../../api/queries/usePayMutation";
import { useGlobalContext } from "../../context/globalContext";
import { DEFAULT_SERVICE_LOGO, emailRegex } from "../../constants";
import { CorporateService } from "../../models/Service";
import { PaymentMethod } from "../../models/payment";
import { ThemeProps } from "../../models/theme";
import {
  digitsOnly,
  formatCurrency,
  getFailureDisplay,
  getStorageToken,
  isErrorResponse,
  isPayableAmount,
  payableAmount,
  unclaimedFields,
} from "../../utils";
import type { FieldClaims } from "../../utils";

// D-01: a template owns its own step sequence, and the two deposit journeys do
// not share one -- the searched journey identifies a customer first. Named
// rather than a bare integer, per payment-journey-step-integrity.md; the
// in-repo precedent is context/schoolFeeContext.tsx:19-27.
export const DEPOSIT_FUND_MANUAL_STEPS = ["details", "confirm"] as const;
export const DEPOSIT_FUND_SEARCH_STEPS = [
  "identify",
  "details",
  "confirm",
] as const;

export type DepositFundStep = (typeof DEPOSIT_FUND_SEARCH_STEPS)[number];

// The codes each journey draws with its own inputs, so the configured slot
// does not draw them a second time. Claimed by PURPOSE: the phone here becomes
// instrument_account_number, the account debited, and for deposit the payer
// and the account holder are the same person.
export const DEPOSIT_FUND_MANUAL_CLAIMS: FieldClaims = [
  "CUSNAME",
  "CUSEMAIL",
  "CUSPHONE",
];

// The searched journey takes name and email from the found customer and never
// draws them, so it claims neither.
export const DEPOSIT_FUND_SEARCH_CLAIMS: FieldClaims = ["CUSPHONE"];

export type FlowTemplateProps = {
  service: CorporateService;
  theme: ThemeProps;
  corporateId: string;
};

// The half of the pay request that differs between the two journeys: a searched
// payment pays for the customer that was found, a manual one for whoever filled
// the form in. Supplied by each template so neither has to ask which it is.
type InstrumentPayload = Record<string, unknown>;

type PanelRenderer = (
  step: DepositFundStep,
  methods: PaymentMethod[],
  configuredFields: React.ReactNode
) => React.ReactNode;

type JourneyProps = FlowTemplateProps & {
  steps: readonly DepositFundStep[];
  // Whether the payer types their own name and email, which is also what makes
  // those two fields required. The searched journey takes both from the customer.
  collectsPayerIdentity: boolean;
  // The field codes this journey draws with its own inputs. The two journeys
  // differ: a single shared list would over-claim on the searched one and
  // silently drop a field the payer should have seen.
  claims: FieldClaims;
  instrumentPayload: () => InstrumentPayload;
  renderPanel: PanelRenderer;
};

// Replaces shared/boxBillPayment.tsx. Shown on both of the searched journey's
// paying steps, so the payer can see who they are paying for beside the amount.
const BillCard = ({ currency }: { currency?: string }) => {
  const { t } = useTranslation();
  const { isSmall } = useDimensions();
  const { customerUser, amount, currentStep } = usePayment();

  const listData = [
    { title: t("payment.form.customer_name"), value: customerUser?.name },
    {
      title: t("payment.form.customer_code"),
      value: customerUser?.customer_business_no || "-",
    },
  ];

  return (
    <Box
      sx={{
        width: isSmall || currentStep === 3 ? "unset" : "40%",
        justifyContent: "center",
        marginBottom: isSmall ? 1 : 4,
      }}
    >
      {currentStep === 2 && (
        <>
          <Typography
            sx={{
              fontWeight: "600",
              fontSize: isSmall ? 16 : 20,
              textAlign: "left",
            }}
          >
            {t("payment.form.stepper_2.payment_details")}
          </Typography>
          <Typography sx={{ fontSize: 11 }}>
            {t("payment.form.stepper_2.payment_desc")}
          </Typography>
        </>
      )}

      <Card>
        <Box
          sx={{ padding: isSmall ? "8px 16px" : "10px 24px" }}
          display="flex"
          marginBottom={1}
          alignItems="center"
          justifyContent="space-between"
        >
          <Box>
            {t("payment.form.amount").toUpperCase()}
            <Typography variant="body1" fontSize={24} fontWeight="700">
              {amount ? formatCurrency(amount, undefined, currency) : "..."}
            </Typography>
          </Box>
          <Chip
            label={t("payment.form.not_paid")}
            variant="filled"
            status={false}
          />
        </Box>
        <Box paddingBottom={2}>
          <StackList spacing={2} list={listData} margin={2} />
        </Box>
      </Card>
    </Box>
  );
};

// Replaces components/paymentForm.tsx. D-04: this is the template's OWN amount
// entry -- a service's configured fields render beside it, never in its place.
const PayerDetailsFields = () => {
  const { t } = useTranslation();
  const { amount, setAmount, name, setName, email, setEmail } = usePayment();
  const [amountError, setAmountError] = useState("");
  const [nameError, setNameError] = useState("");
  const [emailError, setEmailError] = useState("");

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = digitsOnly(e.target.value);
    setAmount(value);

    if (value === "") {
      setAmountError(t("payment.form.errors.amount_required"));
    } else if (!isPayableAmount(value)) {
      setAmountError(t("payment.form.errors.min_amount"));
    } else {
      setAmountError("");
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setName(value);
    setNameError(
      value.trim() === "" ? t("payment.form.errors.name_required") : ""
    );
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.trim();
    setEmail(value);

    if (value === "") {
      setEmailError(t("payment.form.errors.email_required"));
    } else if (!emailRegex.test(value)) {
      setEmailError(t("payment.form.errors.email_invalid"));
    } else {
      setEmailError("");
    }
  };

  return (
    <Box mb={4}>
      <Typography variant="h6" fontWeight="bold" mb={2}>
        {t("payment.form.title")}
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            type="text"
            id="amount"
            label={t("payment.form.amount_label")}
            value={amount}
            onChange={handleAmountChange}
            error={!!amountError}
            helperText={amountError || t("payment.form.amount_hint")}
            variant="outlined"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            type="text"
            id="name"
            label={`${t("payment.form.name_label")}*`}
            value={name}
            onChange={handleNameChange}
            error={!!nameError}
            helperText={nameError || t("payment.form.name_hint")}
            variant="outlined"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            type="email"
            id="email"
            label={`${t("payment.form.email_label")}*`}
            value={email}
            onChange={handleEmailChange}
            error={!!emailError}
            helperText={emailError || t("payment.form.email_hint")}
            variant="outlined"
            sx={{ mt: 2 }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

// The searched journey's amount entry. It is a controlled react-hook-form field
// and is mirrored into payment context, which is what the price and pay calls
// read -- the two stores existed before this template and it does not add one.
const SearchedAmountField = () => {
  const { t } = useTranslation();
  const { control, watch } = useFormContext();
  const { amount, setAmount } = usePayment();
  const entered = watch("customerAmount");

  // MPAY-32393: this ran parseFloat on an unfiltered field and stored
  // String(parsed), so "100.99" was accepted and "0100.50" was renormalised
  // before anyone saw it. The field now holds exactly what the payer typed,
  // filtered to whole units at the keystroke like the manual field beside it,
  // and this effect only mirrors it into the journey state the pay call reads.
  useEffect(() => {
    if (entered === undefined) return;
    setAmount(String(entered));
  }, [entered, setAmount]);

  return (
    <Box>
      <CustomInput
        id="account_number-id"
        control={control}
        name="customerAmount"
        sx={[{ width: "100%" }]}
        variant="outlined"
        label={t("payment.paymentForSearch.amount_label")}
        defaultValue={amount}
        transform={digitsOnly}
        rules={{ required: t("errors.field_required") }}
        placeholder={t("payment.paymentForSearch.amount_placeholder")}
      />
      <Typography sx={{ fontSize: 11 }}>
        {t("payment.paymentForSearch.amount_hint")}
      </Typography>
    </Box>
  );
};

// D-04's designated slot: additive, beside the template's own amount entry and
// never in place of part of it. A mandatory field this renderer cannot draw
// blocks the journey rather than being silently omitted from a payment.
const ConfiguredFieldsSlot = ({
  service,
  claims,
  onBlocked,
  onIncomplete,
  values,
  onChange,
}: {
  service: CorporateService;
  claims: FieldClaims;
  onBlocked: (blocking: string[]) => void;
  onIncomplete: (incomplete: string[]) => void;
  values: Record<string, string>;
  onChange: (fieldId: string, value: string) => void;
}) => {
  const fields = unclaimedFields(service?.submitFields ?? [], claims);

  if (fields.length === 0) return null;

  return (
    <Box mb={4}>
      <ConfiguredFields
        fields={fields}
        values={values}
        onChange={onChange}
        onUnsupported={onBlocked}
        onIncomplete={onIncomplete}
      />
    </Box>
  );
};

// A field the payer typed into and then cleared is not an answer, so it is
// dropped rather than sent as an empty value. Returns nothing at all when
// there is nothing to say, so the pay request omits the key entirely.
const answeredCdata = (values: Record<string, string>) => {
  const answered = Object.entries(values).filter(
    ([, value]) => value.trim() !== ""
  );

  return answered.length > 0 ? { cdata: Object.fromEntries(answered) } : {};
};

// The step machine both deposit templates share. It owns the transitions and
// the two calls. What it does NOT own is which steps exist or what the pay
// request carries, because those are the parts that differ per journey.
const DepositFundJourney = ({
  service,
  theme,
  corporateId,
  steps,
  collectsPayerIdentity,
  claims,
  instrumentPayload,
  renderPanel,
}: JourneyProps) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const token = getStorageToken();
  const form = useForm();
  const [blockedFieldIds, setBlockedFieldIds] = useState<string[]>([]);
  const [incompleteFieldIds, setIncompleteFieldIds] = useState<string[]>([]);
  // Owned here, not in the slot: the answers have to reach the pay call,
  // and state held inside the slot cannot.
  const [configuredValues, setConfiguredValues] = useState<
    Record<string, string>
  >({});

  const {
    currentStep,
    setCurrentStep,
    amount,
    name,
    email,
    selectedMethod,
    phoneNumber,
    error,
    setError,
  } = usePayment();
  const {
    setTotalAmount,
    setTotalFees,
    setInstrumentFee,
    setRegistrationFee,
    setCurrency,
  } = useGlobalContext();

  const payMutation = usePayMutation({ authorisation: token! });
  const priceMutation = usePriceMutation({ authorisation: token! });

  const paymentMethods = useQueryPaymentMethods({
    corporateId,
    enabled: !!token,
    authorisation: token || "",
  });

  const step = steps[currentStep - 1];

  // Lifting the answers out of the slot made them outlive it, so going back
  // to identify and searching a different customer would carry the previous
  // one's answers onto this payment. The slot used to remount and forget.
  useEffect(() => {
    if (step === "identify") setConfiguredValues({});
  }, [step]);
  const isLoading = payMutation.isLoading || priceMutation.isLoading;

  const showFailure = (failure: unknown) =>
    setError(getFailureDisplay(failure, t, i18n.language).message);

  // The PRICE payload. D-HARM-3 and D-HARM-4 scope to the pay request only, so
  // the price call keeps naming the service the way it always has -- the two
  // are separated rather than sharing one literal, because a single object
  // cannot serve two contracts that have diverged (TODO-266).
  const basePayload = useMemo(
    () => ({
      amount: payableAmount(amount),
      corporate_subsidiary_id: service?.corporate_subsidiary_id,
      payment_method_id: selectedMethod?.id as number,
      corporate_service_id: service?.id,
    }),
    [amount, selectedMethod, service]
  );

  // The details step's output, named once. Every later step depends on it and
  // none may proceed without it, which is the step-integrity rule written as a
  // condition rather than left implicit in a step number.
  const hasDetails = () =>
    // The number the body will carry, not a truncating parse of the field.
    isPayableAmount(amount) &&
    (collectsPayerIdentity
      ? name.trim() !== "" &&
        email.trim() !== "" &&
        emailRegex.test(email.trim())
      : true) &&
    selectedMethod !== null &&
    phoneNumber.trim() !== "" &&
    phoneNumber.length === 9 &&
    blockedFieldIds.length === 0 &&
    incompleteFieldIds.length === 0;

  const storePrice = (data: {
    total_fees: string;
    total_amount: string;
    instrument_fee?: string;
    registration_fee?: string;
  }) => {
    setTotalFees(data.total_fees.toString());
    setTotalAmount(data.total_amount.toString());
    // Null-safe: the components only arrive once the CPS half ships, and a
    // bare toString would blank the screen.
    setInstrumentFee(String(data.instrument_fee ?? ""));
    setRegistrationFee(String(data.registration_fee ?? ""));
  };

  const submit = () => {
    if (step === "details") {
      if (!hasDetails()) return;

      priceMutation.mutate(basePayload, {
        onSuccess: data => {
          // The price call resolves WITH an error envelope rather than
          // throwing, so this guard is live -- unlike the pay call's, which
          // usePayMutation made unreachable and this template therefore drops.
          if (isErrorResponse(data)) {
            showFailure(data);
            return;
          }
          storePrice(data);
          setCurrentStep(currentStep + 1);
        },
        onError: showFailure,
      });
      return;
    }

    if (step === "confirm") {
      payMutation.mutate(
        {
          ...basePayload,
          // D-HARM-13. The three fields above it are retired and still sent:
          // see PaymentPayload for why this PR adds without removing.
          core_service_id: service?.core_service_id,
          channel: "App",
          ...instrumentPayload(),
          // Omitted entirely when the service asked nothing, so a payment
          // that carries no answers sends no empty object.
          ...answeredCdata(configuredValues),
        },
        {
          // The caller owns where the payer goes next; the mutation used to
          // navigate from inside api/, the repository's one dependency-law
          // violation.
          onSuccess: data => {
            // The unit the payment was actually made in, kept beside the
            // amounts the outcome screen renders. It is the only value read
            // off the pay response other than the id -- the charge split it
            // also carries is a re-fetched pre-pay quote, not the settled
            // charge, and is deliberately not shown to a payer.
            setCurrency(data.currency);
            navigate(
              `transaction/${data.payment_item_id}?${priceSearch(basePayload)}`,
              { state: { phoneNumber } }
            );
          },
          onError: showFailure,
        }
      );
    }
  };

  if (paymentMethods?.isLoading) return <Loading />;

  if (paymentMethods?.isError) {
    navigate(-1);
    return null;
  }

  const methods = (paymentMethods?.data as PaymentMethod[]) || [];

  // Identification has no forward action of its own: the search result is what
  // advances the journey, so a Continue button here would offer a way past a
  // step whose output nothing has produced yet.
  const showsActions = step !== "identify";

  const configuredFields =
    step === "details" ? (
      <ConfiguredFieldsSlot
        service={service}
        claims={claims}
        onBlocked={setBlockedFieldIds}
        onIncomplete={setIncompleteFieldIds}
        values={configuredValues}
        onChange={(fieldId, value) =>
          setConfiguredValues(previous => ({ ...previous, [fieldId]: value }))
        }
      />
    ) : null;

  return (
    <Container
      justifyContent="center"
      alignItems="center"
      height="70vh"
      sx={{ borderColor: theme?.color_primary }}
    >
      <PaymentHeader isCustomerSearchEnabled={!collectsPayerIdentity} />
      <FormProvider {...form}>
        <form onSubmit={() => null}>
          <Box>
            <StepContainer
              title={t(`payment.step${currentStep}_title`)}
              description={t(
                step === "identify"
                  ? "payment.step0_description"
                  : step === "details"
                  ? "payment.step1_description"
                  : "payment.step2_description"
              )}
            />

            {error && (
              <Alert severity="error" sx={{ margin: 5 }}>
                <AlertTitle>{t("errors.title")}</AlertTitle>
                {error}
              </Alert>
            )}

            {blockedFieldIds.length > 0 && (
              <Alert severity="warning" sx={{ mx: 5, mb: 2 }}>
                {t("configuredFields.unsupported.blocking")}
              </Alert>
            )}

            <MerchantInfo
              merchantName={service?.service_name}
              logoSrc={service?.logo?.url || DEFAULT_SERVICE_LOGO}
              borderColor={theme?.color_primary}
            />

            <Box
              sx={{
                border: 1,
                borderBottom: currentStep !== 1 ? "none" : undefined,
                borderColor: theme?.color_primary,
                borderRadius: 1,
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
                borderBottomRightRadius: currentStep !== 1 ? 0 : 1,
                borderBottomLeftRadius: currentStep !== 1 ? 0 : 1,
                px: { xs: 2, md: 8 },
                py: 4,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: theme?.light_gray,
              }}
            >
              {renderPanel(step, methods, configuredFields)}
            </Box>

            {showsActions && (
              <Box
                sx={{
                  bgcolor: "white",
                  border: 1,
                  borderColor: theme?.color_primary || "grey.300",
                  borderRadius: 2,
                  borderTopLeftRadius: 0,
                  borderTopRightRadius: 0,
                  p: 2,
                  mb: 3,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                <Button
                  onClick={() => navigate(-1)}
                  variant="outlined"
                  disabled={isLoading}
                  sx={{
                    px: 3,
                    py: 1.5,
                    fontWeight: 500,
                    textTransform: "none",
                    borderColor: "red",
                    color: "red",
                  }}
                >
                  {t("payment.actions.cancel")}
                </Button>

                <Box>
                  {currentStep > 1 && (
                    <Button
                      onClick={() => setCurrentStep(currentStep - 1)}
                      variant="outlined"
                      disabled={isLoading}
                      sx={{
                        px: 3,
                        py: 1.5,
                        fontWeight: 500,
                        textTransform: "none",
                        borderColor: theme?.color_primary,
                        color: theme?.color_primary,
                        mr: 3,
                      }}
                    >
                      {t("payment.actions.back")}
                    </Button>
                  )}
                  <Button
                    onClick={submit}
                    disabled={!hasDetails() || isLoading}
                    variant="contained"
                    sx={{
                      bgcolor: hasDetails()
                        ? theme?.color_primary
                        : "warning.main",
                      color: "#fff",
                      "&:hover": {
                        bgcolor: hasDetails()
                          ? theme?.color_primary
                          : "warning.dark",
                      },
                      px: 3,
                      py: 1.5,
                      fontWeight: 500,
                      textTransform: "none",
                    }}
                  >
                    {isLoading ? (
                      t("payment.actions.processing")
                    ) : (
                      <>
                        {step === "details"
                          ? t("payment.actions.continue")
                          : t("payment.actions.confirm")}
                        <ArrowRight sx={{ ml: 1, fontSize: 20 }} />
                      </>
                    )}
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </form>
      </FormProvider>
    </Container>
  );
};

/**
 * DEPOSITFUNDS_MANUAL. The payer types the amount and their own name and email,
 * chooses an instrument, and confirms a priced summary.
 */
export const DepositFundManualTemplate = (props: FlowTemplateProps) => {
  const { t } = useTranslation();
  const { name, email, phoneNumber } = usePayment();

  return (
    <DepositFundJourney
      {...props}
      steps={DEPOSIT_FUND_MANUAL_STEPS}
      collectsPayerIdentity
      claims={DEPOSIT_FUND_MANUAL_CLAIMS}
      instrumentPayload={() => ({
        instrument_account_owner: name,
        instrument_account_number: phoneNumber,
        notification_email: email,
      })}
      renderPanel={(step, methods, configuredFields) => (
        <Paper elevation={2} sx={{ p: 4, width: "100%", maxWidth: 800 }}>
          {step === "details" ? (
            <>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                {t("payment.details_title")}
              </Typography>
              <Typography variant="body1" color="text.secondary" gutterBottom>
                {t("payment.details_description")}
              </Typography>
              <PayerDetailsFields />
              {configuredFields}
              <PaymentMethods payments={methods} />
              <PhoneNumberInputs service={props.service} />
            </>
          ) : (
            <>
              <PaymentSummary currency={props.service?.currency} />
              <ServiceChargeSummary currency={props.service?.currency} />
            </>
          )}
        </Paper>
      )}
    />
  );
};

/**
 * DEPOSITFUNDS_SEARCH. The customer is found by their code first; the bill card
 * then stands beside amount entry so the payer can see who they are paying for.
 */
export const DepositFundSearchTemplate = (props: FlowTemplateProps) => {
  const { t } = useTranslation();
  const { customerUser, selectedWallet, phoneNumber } = usePayment();
  const { isSmall } = useDimensions();

  return (
    <DepositFundJourney
      {...props}
      steps={DEPOSIT_FUND_SEARCH_STEPS}
      collectsPayerIdentity={false}
      claims={DEPOSIT_FUND_SEARCH_CLAIMS}
      instrumentPayload={() => ({
        customer_id: customerUser?.id,
        customer_wallet_id: selectedWallet?.id,
        instrument_account_number: phoneNumber || undefined,
      })}
      renderPanel={(step, methods, configuredFields) => (
        <Paper
          elevation={1}
          sx={{
            p: 4,
            width: "100%",
            maxWidth: step === "details" ? undefined : 800,
            boxShadow: "none",
            bgcolor: props.theme?.light_gray,
          }}
        >
          {step === "identify" && (
            <SearchCustomerForm corporateId={props.corporateId} />
          )}

          {step !== "identify" && (
            <Box
              display={isSmall || step === "confirm" ? "block" : "flex"}
              justifyContent="space-between"
              alignItems="flex-start"
              paddingX={isSmall ? 2 : 10}
            >
              <BillCard currency={props.service?.currency} />
              {step === "confirm" && (
                <ServiceChargeSummary currency={props.service?.currency} />
              )}
              {step === "details" && (
                <Box sx={{ width: isSmall ? "unset" : "40%" }}>
                  <Typography variant="h6" fontWeight="bold" mb={2}>
                    {t("payment.paymentForSearch.step1_title")}
                  </Typography>
                  <SearchedAmountField />
                  {configuredFields}
                  <PaymentMethods payments={methods} />
                  <PhoneNumberInputs service={props.service} isVertical />
                </Box>
              )}
            </Box>
          )}
        </Paper>
      )}
    />
  );
};
